<!--
 * @Author: Tahiti
 * @Date: 2025-10-21 15:03:16
 * @LastEditors: Tahiti
 * @LastEditTime: 2025-12-01 18:12:40
 * @Description: 
 * @FilePath: /weryai-web/components/Tool/VideoUpload.vue
-->
<template>
    <div class="mt-4">
        <div class="w-full flex gap-2">
            <div v-if="!video_url && !singleUploading" @click="triggerSingleFileUpload" @dragover.prevent
                @drop.prevent="handleSingleFileDrop"
                class="h-[127px] w-full max-h-[130px] group text-sub-title hover:text-white flex flex-col items-center justify-center gap-[10px] bg-tool-default-black border border-white/[.06] rounded-[10px] p-[10px] text-center cursor-pointer">
                <div
                    class="flex items-center justify-center flex-col w-[100px] h-[100px] border border-dashed border-white/20 rounded-lg p-2 gap-[10px]">
                    <ui-upload-icon type="video" :hoverable="true" />

                    <p class="text-sub-title text-xs group-hover:text-white">
                        {{ t('tools.videoUploadSingle') }}
                    </p>
                </div>
                <input ref="singleFileInput" type="file" accept="video/*" @change="handleSingleFileSelect"
                    class="hidden" />
            </div>
            <div v-else-if="singleUploading"
                class="h-[127px] w-full max-h-[130px] flex flex-col items-center justify-center bg-item-default rounded-[10px] p-[10px] text-center cursor-pointer">
                <l-dot-spinner v-if="isClient" size="40" speed="0.9"
                    color="var(--el-menu-active-color)"></l-dot-spinner>
            </div>
            <div v-else-if="video_url"
                class="flex flex-col relative h-[240px] w-full max-h-[240px] items-center justify-center bg-item-default rounded-[10px] p-[10px] text-center cursor-pointer">
                <video ref="videoElement" :src="video_url" controls autoplay muted playsinline webkit-playsinline
                    crossorigin="anonymous" class="max-w-full max-h-full rounded-lg cursor-pointer"
                    @loadedmetadata="onVideoLoaded" @click="emitOpenVideoPreview(video_url)" />
                <button
                    class="inline-flex rounded-full items-center justify-center whitespace-nowrap text-base font-medium ring-offset-background duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:text-accent-foreground absolute z-[2] top-1 right-1 h-[22px] w-[22px] bg-[#2E2E2E] text-white group-hover:opacity-100 transition-opacity"
                    @click="clearEditImage">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" fill="none"
                        class="w-3 h-3">
                        <path d="M9 3.68164L3 9.68164" stroke="white" stroke-width="2" stroke-linecap="round"
                            stroke-linejoin="round" />
                        <path d="M3 3.68164L9 9.68164" stroke="white" stroke-width="2" stroke-linecap="round"
                            stroke-linejoin="round" />
                    </svg>
                </button>
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useI18n } from '~/composables/useI18n'
import { debounce, noticeMessagePopup } from '@/utils/common'
import { showNotification } from '@/utils/common'
import { emitOpenVideoPreview, showLoginModal } from '~/utils/emitter'
import axios from 'axios'
import { useUserStore } from '~/stores/user'

const { t, locale } = useI18n()

// 添加 minDuration prop，用于控制最小时长验证（秒）
const props = withDefaults(
    defineProps<{
        minDuration?: number // 最小时长（秒），0 表示不验证
        maxDuration?: number // 最大时长（秒），0 表示不验证
        maxSizeMb?: number // 最大文件大小（MB），0 表示不验证
        tool_type?: string // 工具类型
    }>(),
    {
        minDuration: 0,
        maxDuration: 0,
        maxSizeMb: 0,
        tool_type: '',
    }
)

// video_upscaler：限制上传视频大小不超过 100MB
const VIDEO_UPSCALER_MAX_SIZE_MB = 100
const validateBeforeUpload = (fileList: File[]): boolean => {
    const maxMb =
        props.tool_type === 'video_upscaler'
            ? VIDEO_UPSCALER_MAX_SIZE_MB
            : props.maxSizeMb
    if (!maxMb || maxMb <= 0) return true

    const maxBytes = maxMb * 1024 * 1024
    const tooLarge = fileList?.some(f => f?.size > maxBytes)
    if (tooLarge) {
        // video_upscaler 维持原提示文案，其它工具使用可参数化的通用文案
        if (props.tool_type === 'video_upscaler') {
            showNotification.error(
                t('order_modal.errorVideoUpscalerSizeExceeded')
            )
        } else {
            showNotification.error(
                t('tools.errorVideoSizeExceededCustom', { size: maxMb })
            )
        }
        // 清理已有状态，避免 UI 进入异常态
        video_url.value = ''
        video_duration.value = 0
        video_width.value = 0
        video_height.value = 0
        if (singleFileInput.value) singleFileInput.value.value = ''
        return false
    }
    return true
}

const frame_end_mode = defineModel('frame_end_mode', {
    type: Boolean,
    default: false,
})
const video_url = defineModel('video_url', {
    type: String,
    default: '',
})

const video_duration = defineModel('video_duration', {
    type: Number,
    default: 0,
})

const video_width = defineModel('video_width', {
    type: Number,
    default: 0,
})

const video_height = defineModel('video_height', {
    type: Number,
    default: 0,
})

const singleFileInput = ref<HTMLInputElement | null>(null)
const videoElement = ref<HTMLVideoElement>()

const userStore = useUserStore()
const isLoggedIn = computed(() => userStore.isLoggedIn)
const triggerSingleFileUpload = () => {
    if (!isLoggedIn.value) {
        showLoginModal()
        return
    }
    singleFileInput.value?.click()
}

const handleSingleFileDrop = (event: DragEvent) => {
    if (!isLoggedIn.value) {
        showLoginModal()
        return
    }
    const files = event.dataTransfer?.files
    if (files) {
        const fileArray = Array.from(files)
        handleSingleAutoBatchUpload(fileArray)
    }
}

const uploadSingleFileList = ref<File[]>([])
const singleUploading = ref(false)
// single file upload
const handleSingleFileSelect = (event: Event) => {
    const target = event.target as HTMLInputElement
    const files = target.files as FileList
    const fileArray = Array.from(files)
    handleSingleAutoBatchUpload(fileArray)
}
// @ts-ignore
const config = useRuntimeConfig()

const getVideoMetaFromFile = async (
    file: File
): Promise<{ duration: number; width: number; height: number }> => {
    if (!import.meta.client) {
        return { duration: 0, width: 0, height: 0 }
    }

    return await new Promise((resolve, reject) => {
        const video = document.createElement('video')
        video.preload = 'metadata'
        video.muted = true
        const objectUrl = URL.createObjectURL(file)

        const cleanup = () => {
            URL.revokeObjectURL(objectUrl)
            video.removeAttribute('src')
            // @ts-ignore
            video.load?.()
        }

        video.onloadedmetadata = () => {
            const duration = Number.isFinite(video.duration)
                ? video.duration
                : 0
            const width = Number.isFinite(video.videoWidth)
                ? video.videoWidth
                : 0
            const height = Number.isFinite(video.videoHeight)
                ? video.videoHeight
                : 0
            cleanup()
            resolve({ duration, width, height })
        }

        video.onerror = () => {
            cleanup()
            reject(new Error('Failed to load video metadata'))
        }

        video.src = objectUrl
    })
}

// upload image
const handleSingleAutoBatchUpload = debounce(async (fileList: File[]) => {
    if (!fileList?.length) return

    // size 校验（覆盖：点击选择 + 拖拽上传）
    if (!validateBeforeUpload(fileList)) return

    const firstFile = fileList[0]
    if (!firstFile) return

    // 读取本地视频 metadata（优先在上传前拦截：时长 + 分辨率）
    try {
        const meta = await getVideoMetaFromFile(firstFile)
        const duration = Math.ceil(meta.duration || 0)
        video_duration.value = duration
        video_width.value = meta.width || 0
        video_height.value = meta.height || 0

        // 最小时长校验
        if (props.minDuration > 0 && duration <= props.minDuration) {
            showNotification.error(t('tools.errorVideoDurationTooShort'))
            video_url.value = ''
            video_duration.value = 0
            video_width.value = 0
            video_height.value = 0
            if (singleFileInput.value) singleFileInput.value.value = ''
            return
        }

        // 最大时长校验
        if (props.maxDuration > 0 && duration > props.maxDuration) {
            // 当前项目已有 30 秒文案（用于多处视频工具）
            showNotification.error(t('tools.errorVideoDurationTooLong'))
            video_url.value = ''
            video_duration.value = 0
            video_width.value = 0
            video_height.value = 0
            if (singleFileInput.value) singleFileInput.value.value = ''
            return
        }
    } catch (err) {
        console.error('read video metadata failed:', err)
        // metadata 读取失败时不中断上传，避免误杀；但不会提供分辨率校验能力
    }

    uploadSingleFileList.value = fileList

    // set skeleton screen number
    const formData = new FormData()
    uploadSingleFileList.value.forEach((fileObj: File) => {
        formData.append('files', fileObj)
    })
    singleUploading.value = true
    try {
        // 动态获取最新的 token 和 language，确保登录后能获取到最新值
        const token = import.meta.client
            ? localStorage.getItem('token') || ''
            : ''
        const language = import.meta.client ? locale.value || 'en' : 'en'

        const url = new URL(
            config.public.apiBase + '/api/v1/auth/s3/upload_images'
        )
        url.searchParams.append('app_key', config.public.appKey)

        const res = await axios.post(url.toString(), formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                Authorization: token,
                'Accept-Language': language,
            },
        })
        const { data, status } = res.data
        if (status === 200) {
            video_url.value = data?.object_url_list?.[0]
            singleUploading.value = false
        }
    } catch (err) {
        console.error('upload failed:', err)
    } finally {
        singleUploading.value = false
    }
}, 500)

const onImageError = () => {
    noticeMessagePopup(
        t('tools.imageLoadFailed'),
        t('common.tip'),
        'warning',
        3000,
        ''
    )
    video_url.value = ''
}

const clearEditImage = () => {
    video_url.value = ''
    video_duration.value = 0
    video_width.value = 0
    video_height.value = 0
}

// 视频加载完成，获取时长（向上取整）
const onVideoLoaded = () => {
    if (videoElement.value && videoElement.value.duration) {
        const duration = Math.ceil(videoElement.value.duration)
        video_duration.value = duration
        video_width.value = videoElement.value.videoWidth || 0
        video_height.value = videoElement.value.videoHeight || 0

        // 如果设置了最小时长，进行验证
        if (props.minDuration > 0 && duration <= props.minDuration) {
            showNotification.error(t('tools.errorVideoDurationTooShort'))
            // 清除视频
            video_url.value = ''
            video_duration.value = 0
            video_width.value = 0
            video_height.value = 0
            // 清空文件输入
            if (singleFileInput.value) {
                singleFileInput.value.value = ''
            }
        }

        // 如果设置了最大时长，进行验证
        if (props.maxDuration > 0 && duration > props.maxDuration) {
            showNotification.error(t('tools.errorVideoDurationTooLong'))
            video_url.value = ''
            video_duration.value = 0
            video_width.value = 0
            video_height.value = 0
            if (singleFileInput.value) {
                singleFileInput.value.value = ''
            }
        }
    }
}

const isClient = ref(false)

// 只在客户端注册 ldrs
onMounted(async () => {
    if (typeof window !== 'undefined') {
        try {
            const { dotSpinner } = await import('ldrs')
            dotSpinner.register()
            // 确保注册完成后再设置 isClient，避免组件在注册前渲染
            isClient.value = true
        } catch (error) {
            console.error('Failed to load ldrs:', error)
            // 即使加载失败也设置为 true，避免无限等待
            isClient.value = true
        }
    }
})
</script>
<style scoped lang="less"></style>
